// src/pages/MargSync.js — Marg ERP Sync page
import React, { useState, useEffect, useCallback } from 'react';
import { get, post } from '../utils/api';
import { Spinner, Alert, PageHeader } from '../components/Shared';

export default function MargSync() {
  const [status,     setStatus]     = useState(null);
  const [lastIndex,  setLastIndex]  = useState(0);
  const [loading,    setLoading]    = useState(true);
  const [syncing,    setSyncing]    = useState(false);
  const [msg,        setMsg]        = useState(null);

  // ─── Fetch sync status ──────────────────────────────────────────────────────
  const fetchStatus = useCallback(async () => {
    try {
      const data = await get('/api/erp/status');
      setStatus(data.status);
      setLastIndex(data.lastIndex || 0);
    } catch (e) {
      console.error('Failed to fetch Marg sync status:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    // Auto-refresh status every 30 seconds while page is open
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  // ─── Manual sync trigger ────────────────────────────────────────────────────
  async function handleSyncNow() {
    setSyncing(true);
    setMsg(null);
    try {
      const data = await post('/api/erp/sync', {});
      const r = data.result;
      setMsg({
        type: 'success',
        text: `Sync complete — ${r.invoicesInserted} new invoice(s) added, ${r.partiesUpserted} party record(s) updated`,
      });
      await fetchStatus();
    } catch (e) {
      setMsg({ type: 'error', text: e.message || 'Sync failed' });
    } finally {
      setSyncing(false);
    }
  }

  // ─── Reset index (full re-sync) ─────────────────────────────────────────────
  async function handleResetIndex() {
    if (!window.confirm('This will reset the sync index to 0 and re-fetch all Marg records. Continue?')) return;
    try {
      await post('/api/erp/reset-index', {});
      setMsg({ type: 'info', text: 'Index reset to 0. Running full sync now...' });
      await fetchStatus();
      // Automatically trigger a sync after reset
      setSyncing(true);
      try {
        const data = await post('/api/erp/sync', {});
        const r = data.result;
        setMsg({ type: 'success', text: `Reset + sync complete — ${r.invoicesInserted} invoice(s) added, ${r.partiesUpserted} party record(s) updated` });
      } catch (e) {
        setMsg({ type: 'error', text: 'Reset OK but sync failed: ' + e.message });
      } finally {
        setSyncing(false);
        await fetchStatus();
      }
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
    }
  }

  // ─── Format datetime ────────────────────────────────────────────────────────
  function formatDate(isoString) {
    if (!isoString) return 'Never';
    return new Date(isoString).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  }

  const isHealthy = status?.success === true;
  const hasSynced = status?.lastSync != null;

  if (loading) return <div className="flex justify-center p-12"><Spinner size="lg" /></div>;

  return (
    <div className="p-6 max-w-[1600px]">
      <PageHeader
        title="Marg ERP Sync"
        subtitle="Sync invoices and party records from Marg ERP into DMS"
      />

      {msg && (
        <div className="mb-5">
          <Alert type={msg.type} message={msg.text} onClose={() => setMsg(null)} />
        </div>
      )}

      {/* Status card */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-5">
        <div className="px-5 py-3 border-b bg-gray-50 flex items-center justify-between">
          <h2 className="font-semibold text-gray-700 text-sm">Sync Status</h2>
          <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
            !hasSynced ? 'bg-gray-100 text-gray-500' :
            isHealthy  ? 'bg-green-100 text-green-700' :
                         'bg-red-100 text-red-700'
          }`}>
            {!hasSynced ? 'Never Synced' : isHealthy ? '✓ Healthy' : '✗ Last Sync Failed'}
          </span>
        </div>
        <div className="divide-y divide-gray-100">
          <Row label="Last Sync"     value={formatDate(status?.lastSync)} />
          <Row label="Sync Index"    value={lastIndex} />
          {hasSynced && isHealthy && <>
            <Row label="Invoices Added (last sync)"    value={status.invoicesInserted ?? 0} />
            <Row label="Parties Updated (last sync)"   value={status.partiesUpserted ?? 0} />
            <Row label="Marg Data Status"              value={status.dataStatus || '—'} />
          </>}
          {status?.error && (
            <div className="px-5 py-3 bg-red-50">
              <p className="text-xs font-semibold text-red-600 mb-1">Last Error</p>
              <p className="text-xs text-red-500 font-mono break-all">{status.error}</p>
            </div>
          )}
        </div>
      </div>

      {/* Auto sync notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-5 py-3 mb-5">
        <p className="text-blue-700 text-sm">
          <strong>Auto Sync:</strong> The system automatically syncs with Marg ERP every 15 minutes in the background.
          Use the button below to trigger an immediate sync.
        </p>
      </div>

      {/* Sync Now button */}
      <button
        onClick={handleSyncNow}
        disabled={syncing}
        className="w-full py-3 bg-blue-900 text-white rounded-xl font-semibold text-sm hover:bg-blue-800 disabled:opacity-60 disabled:cursor-not-allowed transition-colors mb-3"
      >
        {syncing ? (
          <span className="flex items-center justify-center gap-2">
            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Syncing with Marg...
          </span>
        ) : '🔄 Sync Now'}
      </button>

      {/* What gets synced */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-5">
        <div className="px-5 py-3 border-b bg-gray-50">
          <h2 className="font-semibold text-gray-700 text-sm">What Gets Synced</h2>
        </div>
        <div className="p-5 space-y-3">
          <SyncItem icon="🏢" title="Party Records" desc="Customer names, addresses, pincodes and phone numbers from Marg are kept up to date automatically" />
          <SyncItem icon="📋" title="Sales Invoices" desc="New sales bills raised in Marg appear at Stage 1 (ERP Received) in the DMS pipeline automatically" />
          <SyncItem icon="⚡" title="Incremental" desc="Only new records since the last sync are fetched — no duplicate processing" />
          <SyncItem icon="🛡️" title="Safe" desc="Invoices already in DMS are never duplicated even if the sync runs multiple times" />
        </div>
      </div>

      {/* Danger zone */}
      <div className="bg-white rounded-xl border border-red-200 overflow-hidden">
        <div className="px-5 py-3 border-b bg-red-50">
          <h2 className="font-semibold text-red-700 text-sm">⚠️ Danger Zone</h2>
        </div>
        <div className="p-5 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-gray-700">Reset Sync Index</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Resets to Index 0 — next sync re-fetches all Marg records. Existing invoices won't duplicate but it may be slow.
            </p>
          </div>
          <button
            onClick={handleResetIndex}
            className="px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg text-xs font-semibold hover:bg-red-100 whitespace-nowrap"
          >
            Reset Index
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between items-center px-5 py-3">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-semibold text-gray-800">{value}</span>
    </div>
  );
}

function SyncItem({ icon, title, desc }) {
  return (
    <div className="flex gap-3">
      <span className="text-xl mt-0.5">{icon}</span>
      <div>
        <p className="text-sm font-semibold text-gray-700">{title}</p>
        <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
      </div>
    </div>
  );
}
